import { BackgroundTasks } from '@/shared/lib/bg-tasks';
import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, FlaskConical, Globe, KeyRound, LifeBuoy, Loader2, LogOut, Moon, ShieldCheck, PanelLeftClose, PanelLeftOpen, Search, Sun } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useGet } from '@/shared/api/hooks';
import { apiError, core } from '@/shared/api/client';
import { can, startSupportSession, useAuth } from '@/shared/lib/auth';
import { cn } from '@/shared/lib/utils';
import { NetxolLogo, NetxolMark } from '@/shared/components/shell/NetxolLogo';
import { IspBrandHeader, PoweredByNetxol, useBrandHead } from '@/shared/components/shell/IspBrand';
import { versionLabel } from '@/shared/lib/version';
import { CheckEmail, useSignupConfig } from '@/modules/signup/SignupShared';
import { useWorkspaceEntry, WorkspaceMenu } from './Workspace';
import { SystemDiagram, SystemLegend } from './SystemDiagram';
import { MfaStep, type MfaChallenge } from './MfaStep';
import { SecurityDialog } from './SecurityDialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { FormDialog } from '@/shared/components/data/FormDialog';
import { NAV, hubFor } from './nav';
import { featureOk, useCountryFeatures } from '@/shared/lib/country';
import { LANGS, setLanguage, t, useT, type Lang } from '@/i18n';
import { setCurrency, setTimezone } from '@/shared/lib/format';
import { applyBrandTheme } from '@/shared/lib/brand';
import { MonitorMenu, NotificationsMenu, ToolsMenu } from '@/shared/components/shell/TopBarTools';
import { AiButton, AiPanelSlot } from '@/modules/ai/AiButton';
import { MaintenanceBar } from '@/shared/components/MaintenanceBar';
/** the contact centre's caller pop-up (loaded only for agents of an ISP that uses it) */
const CallerPopup = lazy(() => import('@/modules/contact-centre/CallerPopup'));

export function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuth((s) => s.accessToken);
  const ready = useAuth((s) => s.ready);
  const loc = useLocation();
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  if (!token) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

export function Guard({ caps, feature, product, children }: { caps: string[]; feature?: string; product?: string; children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const t = useT();
  const features = useCountryFeatures();
  if ((feature || product) && !features) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  // a product feature the ISP does not use: the address is simply not a page of its console (the same as any unknown address)
  if (!featureOk(features, product)) return <div className="p-10 text-center text-muted-foreground" data-testid="not-in-console">{t('ui.not_found')}</div>;
  // a screen of another country (e.g. SEPA or Spanish / Italian VAT at a Pakistani ISP): a clean notice, not a broken page
  if (!featureOk(features, feature)) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-center" data-testid="not-in-country">
        <Globe className="size-8 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t('shell.notInCountry')}</h2>
        <p className="max-w-sm text-sm text-muted-foreground">{t('shell.notInCountryBody')}</p>
      </div>
    );
  }
  if (!can(user, ...caps)) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-center">
        <KeyRound className="size-8 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t('shell.noAccess')}</h2>
        <p className="max-w-sm text-sm text-muted-foreground">{t('shell.noAccessBody', { role: user?.role, caps: caps.join(', ') })}</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function AppShell() {
  const user = useAuth((s) => s.user);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('nx.sidebar') === '1');
  const [palette, setPalette] = useState(false);
  useEffect(() => { localStorage.setItem('nx.sidebar', collapsed ? '1' : '0'); }, [collapsed]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // keep the profile/caps fresh (role changes take effect without re-login)
  const me = useGet<any>('/me', undefined, { refetchInterval: 5 * 60_000 });
  const setUser = useAuth((s) => s.setUser);
  // language: the person's own choice, else the company language (the sign-in page's pick is only for that page)
  useEffect(() => { if (me.data) { setUser(me.data); setCurrency(me.data.tenant?.currency); setTimezone(me.data.tenant?.timezone); setLanguage((me.data.locale ?? me.data.defaultLocale ?? 'en') as Lang, { reload: true }); } }, [me.data, setUser]);
  const t = useT();
  useBrandHead(user?.tenant?.name, user?.tenant?.logo_url);
  // the ISP's colour across the console; back to Netxol's when leaving it (sign-in page)
  useEffect(() => { applyBrandTheme(user?.tenant?.brand_color); return () => applyBrandTheme(null); }, [user?.tenant?.brand_color]);

  const features = useCountryFeatures();   // country-specific entries (SEPA, Spain / Italy VAT …) only for that country's ISPs
  const groups = useMemo(() => NAV.filter((g) => (!g.caps || can(user, ...g.caps)) && (!g.resellerOnly || !!user?.dealerId || can(user, 'crm.dealer.manage')) && featureOk(features, g.feature))
    .map((g) => ({ ...g, items: g.items.filter((i) => (!i.caps || can(user, ...i.caps)) && (!i.resellerOnly || !!user?.dealerId) && featureOk(features, i.feature))
      .map((i) => (i.tabs ? { ...i, tabs: i.tabs.filter((tb) => featureOk(features, tb.feature)) } : i)) })).filter((g) => g.items.length), [user, features]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
    <MaintenanceBar />
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <aside className={cn('flex shrink-0 flex-col border-e bg-card/70 backdrop-blur transition-all', collapsed ? 'w-[68px]' : 'w-64')}>
        <div className="flex h-16 items-center px-4"><Link to="/" className="min-w-0"><IspBrandHeader tenant={user?.tenant} compact={collapsed} badge={user?.tenant?.kind === 'sandbox' ? t('sample.badge') : null} /></Link></div>
        <nav className="scrollbar-slim flex-1 space-y-4 overflow-y-auto px-3 pb-4">
          {groups.map((g) => <SideGroup key={g.key} group={g} collapsed={collapsed} />)}
        </nav>
        <PoweredByNetxol compact={collapsed} version={versionLabel()} />
        <button onClick={() => setCollapsed((c) => !c)} className="flex h-11 items-center gap-2 border-t px-5 text-xs text-muted-foreground hover:text-foreground">
          {collapsed ? <PanelLeftOpen className="size-4" /> : <><PanelLeftClose className="size-4" /> {t('shell.collapse')}</>}
        </button>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        {user?.support && <SupportBanner />}
        {user && can(user, 'cc.agent') && <Suspense fallback={null}><CallerPopup /></Suspense>}
        <TopBar onSearch={() => setPalette(true)} />
        <main className="nx-canvas scrollbar-slim flex-1 overflow-y-auto pb-10"><HubTabs /><Suspense fallback={<PageLoading />}><Outlet /></Suspense></main>
        <AiPanelSlot />
      </div>
      <CommandPalette open={palette} onOpenChange={setPalette} />
    </div>
    </div>
  );
}

/** Netxol support is signed in as this ISP (opened from Netxol Admin): visible for the whole session. */
function SupportBanner() {
  const t = useT(); const user = useAuth((s) => s.user); const clear = useAuth((s) => s.clear);
  return (
    <div className="flex shrink-0 items-center gap-3 bg-amber-500 px-5 py-1.5 text-[12.5px] font-semibold text-amber-950">
      <LifeBuoy className="size-4" />
      <span className="truncate">{t(user?.support?.ro ? 'shell.support_ro' : 'shell.support_full', { name: user?.support?.name, isp: user?.tenant?.name })}</span>
      <button className="ms-auto rounded-md bg-amber-950/10 px-2 py-0.5 hover:bg-amber-950/20" onClick={() => { clear(); window.close(); location.href = '/login'; }}>{t('shell.support_end')}</button>
    </div>
  );
}

/** Entry from Netxol Admin "Sign in as this ISP": the short support session arrives in the address fragment (never sent to a server). */
export function SupportSessionPage() {
  const t = useT(); const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const token = new URLSearchParams(location.hash.slice(1)).get('token');
    history.replaceState(null, '', '/support-session');
    if (!token) { setErr(t('shell.support_missing')); return; }
    core.get('/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { const exp = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp; startSupportSession(token, exp, data); nav('/', { replace: true }); })
      .catch((e) => setErr(apiError(e)));
  }, []); // eslint-disable-line
  return <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">{err ?? <Loader2 className="size-5 animate-spin" />}</div>;
}

/** Tabs of the current hub (menu entry that groups related screens). */
function HubTabs() {
  const t = useT(); const { pathname } = useLocation(); const user = useAuth((s) => s.user);
  const features = useCountryFeatures();
  const hub = hubFor(pathname, features);
  if (!hub) return null;
  const tabs = hub.tabs!.filter((tb) => (!tb.caps || can(user, ...(tb.caps as any))) && featureOk(features, tb.feature));
  if (tabs.length < 2) return null;
  return (
    <div className="sticky top-0 z-20 border-b bg-background/85 px-6 backdrop-blur">
      <nav className="flex gap-1 overflow-x-auto">
        {tabs.map((tb) => {
          // the most specific matching tab is the active one (e.g. /reports/builder, not /reports)
          const matchLen = (x: typeof tabs[number]) => Math.max(pathname === x.to || pathname.startsWith(`${x.to}/`) ? x.to.length : -1, ...(x.also ?? []).map((a) => (pathname.startsWith(a) ? a.length : -1)));
          const best = [...tabs].sort((a, b) => matchLen(b) - matchLen(a))[0];
          const on = best?.to === tb.to;
          return <Link key={tb.to} to={tb.to} className={cn('whitespace-nowrap border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors', on ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>{t(`nav.items.${tb.label}`, { defaultValue: tb.label })}</Link>;
        })}
      </nav>
    </div>
  );
}

function SideGroup({ group, collapsed }: { group: (typeof NAV)[number]; collapsed: boolean }) {
  const user = useAuth((s) => s.user);
  const { pathname } = useLocation();
  const loc = useLocation();
  const t = useT();
  const features = useCountryFeatures();
  const active = group.items.some((i) => (i.end ? loc.pathname === i.to : loc.pathname.startsWith(i.to)));
  const [open, setOpen] = useState(true);
  return (
    <div>
      {!collapsed && (
        <button onClick={() => setOpen((o) => !o)} className={cn('mb-1 flex w-full items-center justify-between px-2 text-[10.5px] font-bold uppercase tracking-[0.12em]', active ? 'text-primary' : 'text-muted-foreground/80')}>
          <span className="flex items-center gap-1.5">{t(`nav.groups.${group.key}`, { defaultValue: group.label })}{group.beta && <BetaTag />}</span><ChevronDown className={cn('size-3 transition-transform', !open && '-rotate-90')} />
        </button>
      )}
      {(open || collapsed) && (
        <div className="space-y-0.5">
          {group.items.map((i) => (
            <NavLink key={i.to} to={i.tabs?.find((tb) => !tb.caps || can(user, ...tb.caps))?.to ?? i.to} end={i.end && !i.tabs} title={t(`nav.items.${i.label}`, { defaultValue: i.label })}
              className={({ isActive: own }) => { const isActive = own || hubFor(pathname, features)?.label === i.label; return cn('group flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-medium transition-colors', isActive && 'active',
                isActive ? 'bg-gradient-to-r from-[var(--nx-brand-from)] to-[var(--nx-brand-to)] text-white shadow-md shadow-[var(--nx-brand-shadow)]' : 'text-foreground/75 hover:bg-gradient-to-r hover:from-[hsl(var(--primary)/0.12)] hover:to-[hsl(var(--primary)/0.04)] hover:text-foreground', collapsed && 'justify-center px-0'); }}>
              <i.icon className={cn('size-4 shrink-0 transition-transform group-hover:scale-110', GROUP_TINT[group.key] ?? '')} />
              {!collapsed && <span className="truncate">{t(`nav.items.${i.label}`, { defaultValue: i.label })}</span>}
              {!collapsed && (i.beta || group.beta) && <BetaTag className="ms-auto" />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function TopBar({ onSearch }: { onSearch: () => void }) {
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const t = useT();
  const changeLang = async (l: Lang | null) => {
    try { await core.patch('/me/preferences', { locale: l }); } catch (e) { toast.error(apiError(e)); return; }
    const eff = (l ?? (user as any)?.defaultLocale ?? 'en') as Lang;
    if (user) setUser({ ...(user as any), locale: l }); setMenu(false); setLanguage(eff, { reload: true });
  };
  const clear = useAuth((s) => s.clear);
  const nav = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [menu, setMenu] = useState(false);
  const [pw, setPw] = useState(false); const [sec, setSec] = useState(false);
  const health = useGet<any>('/system/health', undefined, { refetchInterval: 60_000 });
  useEffect(() => { document.documentElement.classList.toggle('dark', dark); localStorage.setItem('nx.theme', dark ? 'dark' : 'light'); }, [dark]);
  const logout = async () => { await axios.post('/api/core/auth/logout', {}).catch(() => undefined); clear(); nav('/login'); };
  const h = health.data;
  const isDemo = user?.roleKey === 'demo_visitor';
  // the public demo is view-only or full access (Netxol Admin setting); the visitor's role tells which
  const demoFull = isDemo && !user?.caps?.['admin.users'];   // full access takes staff management away, view-only keeps it (writes are refused anyway)
  return (
    <header className="relative z-40 flex h-16 shrink-0 items-center gap-3 border-b bg-card/60 px-5 backdrop-blur">
      <button onClick={onSearch} className="flex h-9 w-full max-w-md items-center gap-2 rounded-lg border bg-background px-3 text-[13px] text-muted-foreground hover:border-primary/40">
        <Search className="size-4 shrink-0" /> <span className="truncate">{t('shell.search')}</span>
        <kbd className="ms-auto shrink-0 whitespace-nowrap rounded border bg-muted px-1.5 text-[10px] font-semibold">Ctrl K</kbd>
      </button>
      <div className="ms-auto flex items-center gap-3">
        {user?.tenant?.kind === 'sandbox' && <span title={t('sample.badge_tip')} className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-bold text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-400 md:inline-flex"><FlaskConical className="size-3.5" />{t('sample.badge')}</span>}
        {isDemo && <span title={t(demoFull ? 'shell.demoFullTip' : 'shell.demoTip')} className="hidden whitespace-nowrap rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-1 text-[11px] font-bold text-white lg:inline">{t(demoFull ? 'shell.demoFull' : 'shell.demo')}</span>}
        {h && (
          <div className="hidden items-center gap-3 whitespace-nowrap rounded-lg border bg-background px-3 py-1.5 text-[11.5px] font-medium 2xl:flex">
            <Dot ok={h.core?.ok} label={t('shell.status.core')} /><Dot ok={h.database?.ok} label={t('shell.status.db')} /><Dot {...radiusState(h.radius)} label={t('shell.status.radius', { n: h.radius?.online ?? 0 })} /><Dot ok={h.nmm?.ok} label={t('shell.status.nmm')} /><Dot ok={h.acs?.ok} label={t('shell.status.acs')} />
          </div>
        )}
        <BackgroundTasks /><AiButton /><ToolsMenu /><MonitorMenu /><NotificationsMenu />
        <Button variant="ghost" size="icon-sm" title={t('shell.theme')} onClick={() => setDark((d) => !d)}>{dark ? <Sun className="size-4" /> : <Moon className="size-4" />}</Button>
        <div className="relative">
          <button onClick={() => setMenu((m) => !m)} className="flex items-center gap-2.5 rounded-lg px-2 py-1 hover:bg-muted">
            <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-[var(--nx-brand-from)] to-[var(--nx-brand-to)] text-xs font-bold text-white">{initials(user?.name)}</div>
            <div className="hidden text-start leading-tight md:block">
              <div className="text-[13px] font-semibold">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground">{user?.role} · {user?.tenant?.name}</div>
            </div>
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
          {menu && (
            <div className="absolute end-0 top-12 z-50 w-60 rounded-xl border bg-popover p-1.5 shadow-popover" onMouseLeave={() => setMenu(false)}>
              <div className="flex items-center gap-2 px-2.5 pb-1 pt-1.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground"><Globe className="size-3.5" />{t('language.label')}</div>
              {LANGS.map((l) => (
                <button key={l} className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm hover:bg-muted" onClick={() => changeLang(l)}>
                  <span className="w-6 text-[10.5px] font-bold uppercase text-muted-foreground">{l}</span>{t(`language.${l}`)}
                  {(user as any)?.locale === l && <Check className="ms-auto size-4 text-primary" />}
                </button>
              ))}
              <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted" onClick={() => changeLang(null)}>
                <span className="w-6" />{t('language.default', { lang: t(`language.${(user as any)?.defaultLocale ?? 'en'}`) })}{!(user as any)?.locale && <Check className="ms-auto size-4 text-primary" />}
              </button>
              <div className="my-1 border-t" />
              {!isDemo && !user?.support && <WorkspaceMenu onDone={() => setMenu(false)} />}
              {!isDemo && <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => { setPw(true); setMenu(false); }}><KeyRound className="size-4" /> {t('shell.changePassword')}</button>}
              {!isDemo && <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => { setSec(true); setMenu(false); }}><ShieldCheck className="size-4" /> {t('mfa.account_title')}</button>}
              <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-rose-600 hover:bg-rose-500/10" onClick={logout}><LogOut className="size-4" /> {t('shell.signOut')}</button>
              <div className="mt-1 border-t px-2.5 pt-1.5 text-[10.5px] text-muted-foreground">Netxol One {versionLabel()}</div>
            </div>
          )}
        </div>
      </div>
      {sec && <SecurityDialog onClose={() => setSec(false)} />}
      <FormDialog open={pw} onOpenChange={setPw} title={t('shell.passwordDialog.title')} description={t('shell.passwordDialog.description')} submitLabel={t('shell.passwordDialog.submit')}
        fields={[{ name: 'current', label: t('shell.passwordDialog.current'), type: 'password', required: true, span: 2 }, { name: 'next', label: t('shell.passwordDialog.next'), type: 'password', required: true, span: 2 }]}
        onSubmit={async (v) => { try { await core.post('/me/password', v); toast.success(t('shell.passwordDialog.done')); setPw(false); } catch (e) { toast.error(apiError(e)); } }} />
    </header>
  );
}

/** RADIUS for this ISP: grey before it has a BNG, green with a log-in in the last day, amber when its BNGs are quiet. */
const radiusState = (r?: { lastAuth?: string | null; nas?: number }) => {
  if (!r?.nas) return { ok: undefined, idle: true, tip: t('shell.status.radius_none') };
  const recent = !!r.lastAuth && Date.now() - new Date(r.lastAuth).getTime() < 86_400_000;
  return recent ? { ok: true } : { ok: false, warn: true, tip: t('shell.status.radius_quiet') };
};
const Dot = ({ ok, label, idle, warn, tip }: { ok?: boolean; label: string; idle?: boolean; warn?: boolean; tip?: string }) => (
  <span className="flex items-center gap-1.5" title={tip}><span className={cn('size-2 rounded-full', ok ? 'bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-500/20'
    : idle ? 'bg-slate-400 shadow-[0_0_0_3px] shadow-slate-400/20' : warn ? 'bg-amber-500 shadow-[0_0_0_3px] shadow-amber-500/20' : 'bg-rose-500 shadow-[0_0_0_3px] shadow-rose-500/20')} />{label}</span>
);
const initials = (n?: string) => (n ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState('');
  const [deb, setDeb] = useState('');
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  const t = useT();
  useEffect(() => { const h = setTimeout(() => setDeb(q), 200); return () => clearTimeout(h); }, [q]);
  useEffect(() => { if (!open) setQ(''); }, [open]);
  const res = useGet<any[]>(deb.length >= 2 ? '/search' : null, { q: deb });
  const features = useCountryFeatures();
  const pages = NAV.filter((g) => (!g.resellerOnly || !!user?.dealerId || can(user, 'crm.dealer.manage')) && featureOk(features, g.feature)).flatMap((g) => g.items.filter((i) => (!i.resellerOnly || !!user?.dealerId) && featureOk(features, i.feature)).flatMap((i) => (i.tabs ? i.tabs.map((tb) => ({ ...i, to: tb.to, label: tb.label, caps: tb.caps, feature: tb.feature })) : [i])).filter((i) => (!g.caps || can(user, ...g.caps)) && (!i.caps || can(user, ...(i.caps as any))) && featureOk(features, i.feature)))
    .filter((i) => q && (i.label.toLowerCase().includes(q.toLowerCase()) || t(`nav.items.${i.label}`, { defaultValue: i.label }).toLowerCase().includes(q.toLowerCase()))).slice(0, 5);
  const go = (to: string) => { onOpenChange(false); nav(to); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[18%] translate-y-0 gap-0 p-0 sm:max-w-xl">
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="size-4 text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('palette.placeholder')} className="h-12 flex-1 bg-transparent text-sm outline-none" />
          {res.isFetching && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {pages.map((p) => (
            <button key={p.to} onClick={() => go(p.to)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm hover:bg-muted"><p.icon className="size-4 text-muted-foreground" /> {t(`nav.items.${p.label}`, { defaultValue: p.label })}<span className="ms-auto text-[11px] text-muted-foreground">{t('palette.page')}</span></button>
          ))}
          {(res.data ?? []).map((r) => (
            <button key={r.type + r.id} onClick={() => go(r.href)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start hover:bg-muted">
              <span className="w-16 shrink-0 rounded bg-primary-soft px-1.5 py-0.5 text-center text-[10px] font-bold uppercase text-primary">{t(`palette.types.${r.type}`, { defaultValue: r.type })}</span>
              <span className="min-w-0"><span className="block truncate text-sm font-medium">{r.title}</span><span className="block truncate text-xs text-muted-foreground">{r.subtitle}</span></span>
            </button>
          ))}
          {deb.length >= 2 && !res.isFetching && !res.data?.length && !pages.length && <div className="px-3 py-8 text-center text-sm text-muted-foreground">{t('palette.none', { q: deb })}</div>}
          {deb.length < 2 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">{t('palette.hint')}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Sign-in fields: a clear white box with a visible border in light mode (browser autofill must not grey it out). */
const AUTH_INPUT = 'nx-auth-input h-11 border-slate-300 bg-card shadow-sm focus-visible:border-primary dark:border-white/15';

export function LoginPage() {
  const setSession = useAuth((s) => s.setSession);
  const t = useT();
  const token = useAuth((s) => s.accessToken);
  const ready = useAuth((s) => s.ready);
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState(() => new URLSearchParams(loc.search).get('email') ?? '');
  const [password, setPassword] = useState('');
  // company sign-in: an e-mail domain that signs in at the ISP's SAML identity provider skips the password field
  const [sso, setSso] = useState<{ provider: string; url: string } | null>(null);
  const ssoError = new URLSearchParams(loc.search).get('sso_error');
  const discover = async (e: string) => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setSso(null); return; }
    try { const { data } = await axios.post('/api/core/auth/sso/discover', { email: e }); setSso(data.method === 'saml' ? { provider: data.provider, url: data.url } : null); } catch { setSso(null); }
  };
  const signup = useSignupConfig();
  const [unverified, setUnverified] = useState(false);
  const [sample, setSample] = useState(false);
  const entry = useWorkspaceEntry(new URLSearchParams(loc.search).get('next') || '/');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mfa, setMfa] = useState<(MfaChallenge & { sample: boolean }) | null>(null);
  const next = new URLSearchParams(loc.search).get('next') || '/';
  const [demoInfo, setDemoInfo] = useState<{ enabled?: boolean; fullAccess?: boolean; refreshing?: boolean }>({});
  const demo = !!demoInfo.enabled;
  useEffect(() => { axios.get('/api/core/auth/demo').then((r) => setDemoInfo(r.data ?? {})).catch(() => undefined); }, []);
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  if (token) return <Navigate to={next} replace />;
  const enterDemo = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await axios.post('/api/core/auth/demo'); setSession(data); nav(next, { replace: true }); }
    catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  const finish = (data: any, viaSample: boolean) => { if (viaSample) entry.go(data); else { setSession(data); nav(next, { replace: true }); } };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const { data } = sample ? await axios.post('/api/core/auth/sample', { email, password })
        : await axios.post('/api/core/auth/login', { email, password }, { headers: { 'Accept-Language': localStorage.getItem('nx.lang') ?? 'en' } });
      // two-factor: the second step (or its setup) comes before the session
      if (data.mfaRequired || data.mfaEnrollRequired) { setMfa({ ...data, sample }); return; }
      finish(data, sample);
    } catch (e2) { setErr(apiError(e2)); setUnverified((e2 as any)?.response?.data?.message?.startsWith('Confirm your e-mail')); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.4fr_1fr]">
      <div className="relative hidden h-screen overflow-hidden bg-[#0b1530] p-10 text-white lg:sticky lg:top-0 lg:flex lg:flex-col xl:p-12">
        <div className="absolute -start-40 -top-40 size-[520px] rounded-full bg-blue-600/30 blur-3xl" />
        <div className="absolute -bottom-48 end-0 size-[520px] rounded-full bg-violet-600/25 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <NetxolMark tone="dark" className="h-10" />
          <div className="text-xl font-bold">Netxol <span className="text-blue-400">One</span></div>
        </div>
        <div className="relative mt-8 space-y-3">
          <h1 className="whitespace-nowrap text-[2.15rem] font-bold leading-tight tracking-tight xl:text-[2.5rem]">{t('login.heroTitle')}</h1>
          <p className="max-w-xl text-[15px] leading-relaxed text-blue-100/80">{t('login.heroBody')}</p>
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center py-5"><SystemDiagram /></div>
        <div className="relative"><SystemLegend /></div>
      </div>
      <div className="flex items-center justify-center p-6">
        {mfa ? <div className="w-full max-w-sm"><MfaStep challenge={mfa} onDone={(d) => finish(d, mfa.sample)} onCancel={() => setMfa(null)} />{entry.overlay}</div> : <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="lg:hidden"><NetxolLogo /></div>
          <div className="flex items-start justify-between gap-2"><div><h2 className="text-2xl font-bold tracking-tight">{t('login.title')}</h2><p className="mt-1 text-sm text-muted-foreground">{t('login.subtitle')}</p></div><LoginLang /></div>
          <label className="block space-y-1.5"><span className="text-xs font-semibold">{t('login.email')}</span><Input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} onBlur={(e) => void discover(e.target.value)} placeholder="you@company.com" className={AUTH_INPUT} /></label>
          {ssoError && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{t('sso.sso_error', { m: ssoError })}</div>}
          {sso ? <Button type="button" className="h-11 w-full text-sm" onClick={() => location.assign(sso.url)}>{t('sso.continue_with', { n: sso.provider })}</Button> : <>
          <label className="block space-y-1.5"><span className="text-xs font-semibold">{t('login.password')}</span><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={AUTH_INPUT} /></label>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm"><input type="checkbox" checked={sample} onChange={(e) => setSample(e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[hsl(var(--primary))]" />
            <span className="min-w-0"><span className="block whitespace-nowrap font-medium">{t('sample.login')}</span><span className="block whitespace-nowrap text-xs text-muted-foreground">{t('sample.login_hint')}</span></span></label>
          {err && !unverified && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{err}</div>}
          {unverified && <CheckEmail email={email} />}
          <Button type="submit" className="h-11 w-full text-sm" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin" />} {t('login.submit')}</Button></>}
          {signup?.enabled && <p className="text-center text-sm text-muted-foreground">{t('signup.new_here')} <Link to="/signup" className="font-semibold text-primary">{t('signup.create')}</Link></p>}
          {demo && (
            <div className="space-y-2 rounded-xl border border-dashed border-violet-500/40 bg-violet-500/5 p-3">
              <Button type="button" variant="outline" className="h-10 w-full text-sm" disabled={busy || demoInfo.refreshing} onClick={enterDemo}>{t(demoInfo.fullAccess ? 'login.demoFull' : 'login.demo')}</Button>
              <p className="text-center text-[11px] text-muted-foreground">{t(demoInfo.refreshing ? 'login.demoRefreshing' : demoInfo.fullAccess ? 'login.demoFullHint' : 'login.demoHint')}</p>
            </div>
          )}
          <p className="text-center text-[11px] text-muted-foreground">{t('login.footer')}</p>
          <p className="text-center text-[10.5px] text-muted-foreground/70" data-testid="login-version">Netxol One {versionLabel()}</p>
          {entry.overlay}
        </form>}
      </div>
    </div>
  );
}

/** Language picker on the sign-in page (before the user's own preference is known). */
function LoginLang() {
  const t = useT();
  const [, force] = useState(0);
  return (
    <div className="flex shrink-0 items-center gap-0.5 rounded-lg border p-0.5 text-[11px] font-bold">
      {LANGS.map((l) => (
        <button key={l} type="button" title={t(`language.${l}`)} onClick={() => { setLanguage(l); force((x) => x + 1); }}
          className={cn('rounded-md px-1.5 py-0.5 uppercase', (localStorage.getItem('nx.lang') ?? 'en') === l ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground')}>{l}</button>
      ))}
    </div>
  );
}

/** Each module keeps its own colour in the navigation (white when the item is active). */
const GROUP_TINT: Record<string, string> = {
  home: 'text-blue-500 group-[.active]:text-white', nmm: 'text-violet-500 group-[.active]:text-white', acs: 'text-fuchsia-500 group-[.active]:text-white',
  crm: 'text-sky-500 group-[.active]:text-white', billing: 'text-emerald-500 group-[.active]:text-white', aaa: 'text-amber-500 group-[.active]:text-white',
  erp: 'text-teal-500 group-[.active]:text-white', hr: 'text-rose-500 group-[.active]:text-white', dealer: 'text-orange-500 group-[.active]:text-white',
  admin: 'text-slate-500 group-[.active]:text-white',
};

/** Shown while a page's code is fetched the first time (pages load on demand). */
function PageLoading() {
  return <div className="flex items-center justify-center py-24 text-xs text-muted-foreground" data-testid="page-loading"><span className="me-2 size-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />{t('common.loading')}</div>;
}

/** "Beta" tag in the ISP's brand colours (white ring so it also reads on the active, gradient menu item). */
function BetaTag({ className }: { className?: string }) {
  const t = useT();
  return <span className={cn('shrink-0 rounded-full bg-gradient-to-r from-[var(--nx-brand-from)] to-[var(--nx-brand-to)] px-1.5 py-px text-[9.5px] font-bold uppercase leading-[14px] tracking-wide text-white ring-1 ring-white/50', className)}>{t('nav.beta')}</span>;
}
